import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from 'utils/cn'

type HelpTooltipProps = {
  label: string
  description: string
  className?: string
}

export function HelpTooltip({ label, description, className }: HelpTooltipProps) {
  const id = useId()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<number>()
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  const show = () => {
    window.clearTimeout(closeTimer.current)
    setOpen(true)
  }
  const hide = () => {
    window.clearTimeout(closeTimer.current)
    setOpen(false)
  }
  const hideAfterHover = () => {
    if (document.activeElement === buttonRef.current) return
    window.clearTimeout(closeTimer.current)
    // ボタンから説明文へポインターを移せるよう、間隔を設ける。
    closeTimer.current = window.setTimeout(() => setOpen(false), 150)
  }

  useEffect(() => () => window.clearTimeout(closeTimer.current), [])

  useLayoutEffect(() => {
    if (!open) return

    const updatePosition = () => {
      if (!buttonRef.current || !tooltipRef.current) return
      const anchor = buttonRef.current.getBoundingClientRect()
      const tooltip = tooltipRef.current.getBoundingClientRect()
      const margin = 12
      const below = anchor.bottom + 8
      const top = below + tooltip.height <= window.innerHeight - margin
        ? below
        : Math.max(margin, anchor.top - tooltip.height - 8)
      const left = Math.max(margin, Math.min(
        anchor.left + anchor.width / 2 - tooltip.width / 2,
        window.innerWidth - tooltip.width - margin,
      ))
      setPosition({ top, left })
    }
    const dismissOutside = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !buttonRef.current?.contains(event.target) &&
        !tooltipRef.current?.contains(event.target)
      ) setOpen(false)
    }
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setOpen(false)
      }
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    document.addEventListener('pointerdown', dismissOutside)
    document.addEventListener('keydown', dismissOnEscape)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
      document.removeEventListener('pointerdown', dismissOutside)
      document.removeEventListener('keydown', dismissOnEscape)
    }
  }, [open, description])

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
        className={cn(
          'relative inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border/80 bg-white text-[11px] font-semibold text-muted transition hover:border-accent hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
          className,
        )}
        onMouseEnter={show}
        onMouseLeave={hideAfterHover}
        onFocus={show}
        onBlur={hide}
        onClick={(event) => {
          event.stopPropagation()
          show()
        }}
      >
        <span aria-hidden="true">?</span>
      </button>
      {open && createPortal(
        <div
          ref={tooltipRef}
          id={id}
          role="tooltip"
          style={position}
          className="fixed z-[100] w-72 max-w-[calc(100vw-24px)] whitespace-normal rounded-xl border border-border bg-white px-3 py-2 text-left text-xs font-normal leading-relaxed text-ink shadow-lg"
          onMouseEnter={show}
          onMouseLeave={hideAfterHover}
        >
          {description}
        </div>,
        document.body,
      )}
    </>
  )
}
